//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import React from "react";
import { QueryRenderer, ReadyState } from "react-relay";
// web-settings-event-color-coding-renderer imports
import { query } from "./WebSettingsEventColorCodingRelay.gql";
// components
import { Loader } from "@clockwise/web-commons/src/components/loader";
import { WebSettingsContainer } from "@components/web-settings/web-settings-container";
import { EventColoringSettings } from "@components/event-coloring-settings";

// util
import { handleReadyStateError } from "@util/relay.util";
import { getCurrentOrg } from "@util/org.util";
import { page, PageEvents, track, TrackingEvents } from "@util/analytics.util";
// constants
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";

// state
import { getCurrentEnvironment } from "@src/state/relay-environment";
// material-ui
import { Typography } from "@material-ui/core";

export interface IRendererProps {}

export interface IRendererState {
  key: number;
  loading: boolean;
}

//////////////////
// COMPONENT
//////////////////
export class WebSettingsEventColorCodingRelay extends React.Component<
  IRendererProps,
  IRendererState
> {
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  private shouldForce = true;

  private cachedOrg?: ISchema.IOrg;

  constructor(props: IRendererProps) {
    super(props);
    this.state = {
      key: 0,
      loading: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Life-Cycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    page(PageEvents.WEB_SETTINGS_EVENT_COLOR_CODING);
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  // private refetch = () => {
  //   this.shouldForce = true;
  //   this.setState({ key: this.state.key + 1 });
  // };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const environment = getCurrentEnvironment();

    return (
      <QueryRenderer
        key={this.state.key}
        environment={environment}
        query={query}
        variables={{}}
        cacheConfig={{ force: this.shouldForce }}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "WebSettingsEventColorCodingRenderer");
            return null;
          }

          const viewer = readyState.props && readyState.props.viewer;
          const org = getCurrentOrg(viewer);

          if (org) {
            this.cachedOrg = org;
          }

          if (!this.cachedOrg || this.state.loading) {
            return <Loader />;
          }

          this.shouldForce = false;

          return (
            <WebSettingsContainer>
              <Typography variant="h1" className="cw-mb-9">
                Event color coding
              </Typography>
              <Typography variant="h3">
                Would you like Clockwise to categorize and color-code your meetings?
              </Typography>
              <Typography variant="subtitle1" className="cw-mb-5">
                See your priorities at a glance with automatic meeting categorization and
                color-coding from Clockwise. Use the default colors or configure them to your
                personal preferences.{" "}
                <a
                  href={articleUrls.colors}
                  target="_blank"
                  onClick={() =>
                    track(TrackingEvents.WEB_SETTINGS.EVENT_COLOR_CODING.LEARN_MORE_CLICKED)
                  }
                >
                  Learn more
                </a>
                .
              </Typography>
              <EventColoringSettings org={org} isWebSettings />
            </WebSettingsContainer>
          );
        }}
      />
    );
  }
}
